import React from 'react';

interface Props {
  isLoading: boolean;
  error?: Error | null;
  retry?: () => void;
  children: React.ReactNode;
  loadingMessage?: string;
  showSpinner?: boolean;
  errorMessage?: string;
}

export const LoadingState: React.FC<Props> = ({
  isLoading,
  error,
  retry,
  children,
  loadingMessage = 'Loading...',
  showSpinner = true,
  errorMessage,
}) => {
  if (error) {
    return (
      <div className="loading-error p-3 text-center">
        <div className="alert alert-danger">
          <p className="mb-2">{errorMessage || error.message}</p>
          {retry && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={retry}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-state p-3 text-center">
        {showSpinner && (
          <div className="spinner-border text-primary mb-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        )}
        <p className="text-muted mb-0">{loadingMessage}</p>
      </div>
    );
  }

  return <>{children}</>;
};

export const LoadingSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'md' }) => {
  const sizeClass = {
    sm: '',
    md: ' spinner-border-md',
    lg: ' spinner-border-lg',
  }[size];

  return (
    <div className="d-flex justify-content-center align-items-center p-3">
      <div className={`spinner-border text-primary${sizeClass}`} role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
};

interface RetryProps {
  message: string;
  onRetry: () => void;
  retryCount?: number;
  maxRetries?: number;
}

export const RetryPrompt: React.FC<RetryProps> = ({
  message,
  onRetry,
  retryCount = 0,
  maxRetries = 3,
}) => {
  const remainingRetries = maxRetries - retryCount;

  return (
    <div className="retry-prompt p-3 text-center">
      <p className="text-muted mb-2">{message}</p>
      {remainingRetries > 0 && (
        <button
          className="btn btn-primary btn-sm"
          onClick={onRetry}
          disabled={retryCount >= maxRetries}
        >
          Retry ({remainingRetries} {remainingRetries === 1 ? 'attempt' : 'attempts'} remaining)
        </button>
      )}
      {remainingRetries <= 0 && (
        <p className="text-danger">
          Maximum retry attempts reached. Please try again later.
        </p>
      )}
    </div>
  );
};